<template>
  <li>
    <div class="row" v-if="notEditingDocket">
      <div class="col-sm-6 col-md-3">
        <p class="material-details" v-if="docket.number">
          <strong>Docket Number</strong><br />
          {{ decoratedDocket.number }}
        </p>
      </div>
      <div class="col-sm-6 col-md-3">
        <p class="material-details" v-if="docket.number">
          <strong>Net Weight</strong><br />
          {{ decoratedDocket.weight }}
        </p>
      </div>
      <div class="col-sm-6 col-md-3 col-lg-2">
        <p class="material-details">
          <strong>Status</strong><br />
          <span :class="decoratedDocket.status_class">{{ decoratedDocket.status_text }}</span>
        </p>
      </div>
      <div class="col-sm-6 col-md-3 col-lg-4">
        <p class="material-details truncate">
          <strong>File</strong><br />
          <a :href="urlToDownloadDocket">{{ decoratedDocket.photo_name}}</a>
        </p>
      </div>
    </div>

    <p class="material-details" v-if="docketIsResubmit && notEditingDocket">
      <strong>Resubmit File</strong><br />
      {{ docket.rejection_reason }}
    </p>

    <div class="row" v-if="editingDocket">
      <div class="col-sm-6 col-md-3">
        <p class="material-details truncate">
          <strong>Docket Number</strong><br />
          <input type="text" name="number" class="form-control" v-model="number">
        </p>
      </div>
      <div class="col-sm-6 col-md-3">
        <p class="material-details truncate">
          <strong>Net Weight</strong><br />
          <input type="number" name="number" class="form-control" v-model="weight">
        </p>
      </div>

      <div class="col-sm-12 col-md-6">
        <p class="material-details truncate" style="margin: 0">
          <strong class="select-label">Docket Image</strong>
        </p>

        <div class="input-group">
          <input type="text" class="form-control" :value="file" disabled>
          <span class="input-group-btn">
            <label class="btn btn-info btn-sm" :for="`docket-${identifiers.id}`">
              <input type="file" class="sr-only" name="file" :id="`docket-${identifiers.id}`" accept=".jpg,.jpeg,.png,.gif" @change="handleFileSelection">
              <i class="fa fa-upload"></i>
            </label>
          </span>
        </div>
      </div>
    </div>

    <ul v-if="canSeeDocketButtons" class="load-actions">
      <li>
        <a
          @click="handlePreviewDocket"
          class="btn btn-circle btn-info"
          :class="{ 'disabled': docketIsBeingPreviewed }">
          <i class="fa fa-eye"></i>
        </a>
      </li>

      <li v-if="canModifyDocket">
        <a @click="handleToggleEditDocket" class="btn btn-circle btn-info"><i class="fa fa-pencil"></i></a>
      </li>

      <li v-if="canDeleteDocket">
        <ladda-button
          :on-click="handleDeleteDocket"
          class="btn btn-circle btn-danger">
          <i class="fa fa-trash-alt"></i>
        </ladda-button>
      </li>

      <li v-if="canRejectDocket">
        <ladda-button
          v-if="docketIsPending"
          :on-click="handleRejectDocket"
          class="btn btn-circle alert-reason"
          type="warning">
          <i class="fa fa-times"></i>
        </ladda-button>
      </li>

      <li v-if="canApproveDocket">
        <ladda-button
          v-if="docketIsPending"
          :on-click="handleApproveDocket"
          class="btn btn-circle btn-success">
          <i class="fa fa-check"></i>
        </ladda-button>
      </li>
    </ul>

    <div v-if="canSaveDocket" class="load-actions m-t-sm">
      <a @click="handleToggleEditDocket" class="btn btn-danger btn-sm">Cancel</a>
      <ladda-button :on-click="handleSaveDocket" size="small" type="success">Save</ladda-button>
    </div>
  </li>
</template>

<script>
  import { mapActions, mapMutations, mapState } from 'vuex'
  import { LaddaButton } from 'components/forms'
  import { notify, alert, url } from 'utils'
  import {
    SET_DOCKET, ADD_DOCKET, REMOVE_DOCKET, SET_WEIGHBRIDGE_ATTRIBUTE, SET_DOCKET_ATTRIBUTE, SET_LOAD_ATTRIBUTE, ADD_LOAD
  } from 'vx/mutations'
  import { docketStateMapper, docketActionsHelpers } from 'mixins/weighbridge'
  import { jobHelpers } from 'mixins/jobs'
  import { DocketDecorator } from 'decorators'
  import moment from 'moment'

  export default {
    props: ['docket'],

    mixins: [docketStateMapper, docketActionsHelpers, jobHelpers],

    data () {
      return {
        editingDocket: false
      }
    },

    components: {
      LaddaButton
    },

    computed: {
      ...mapState('weighbridge', ['weighbridge']),
      ...mapState('weighbridge/docket', { previewingDocket: 'docket' }),

      /**
       * Identifiers used for modifying docket within weighbridge via the state mapper.
       *
       * @return object
       */
      identifiers () {
        return {
          id: this.docket.id
        }
      },

      /**
       * Determine if we can save the docket.
       *
       * @return boolean
       */
      canSaveDocket () {
        if (this.notEditingDocket) {
          return false
        }

        return this.canModifyDocket && (this.$user.canManageLoadChecks || this.docketsLoadBelongsToCarrier)
      },

      /**
       * Determine if we can see the docket actions.
       *
       * @return boolean
       */
      canSeeDocketButtons () {
        if (this.editingDocket) {
          return false
        }

        return true
      },

      /**
       * Determine if docket belongs to the logged in user if they are a carrier.
       *
       * @return boolean
       */
      docketsLoadBelongsToCarrier () {
        return this.$user.isCarrier && this.$user.id === this.load.carrier_id
      },

      /**
       * Determine if we can delete the docket.
       *
       * @return boolean
       */
      canDeleteDocket () {
        return this.canModifyDocket && this.$user.canManageLoadChecks
      },

      /**
       * Determine if we can approve the docket.
       *
       * @return boolean
       */
      canApproveDocket () {
        return this.canModifyDocket && this.$user.canManageLoadChecks
      },

      /**
       * Determine if we can reject the docket.
       *
       * @return boolean
       */
      canRejectDocket () {
        return this.canModifyDocket && this.$user.canManageLoadChecks
      },

      /**
       * Determine if we can modify a docket.
       *
       * @return boolean
       */
      canModifyDocket () {
        if (this.haveJob && (this.jobIsCompleted || this.jobIsCancelled)) {
          return false
        }

        // If the user is a carrier we make sure the load for this docket belongs to
        // the carrier and that the docket is a resubmit.
        if (this.$user.isCarrier && this.docketsLoadBelongsToCarrier) {
          return this.docketIsResubmit && this.docketIsBeingPreviewed

        // If the user is a client then they are unable to modify dockets.
        } else if (this.$user.isClient) {
          return false
        }

        return this.docketIsBeingPreviewed
      },

      /**
       * Determine if docket is being previewed.
       *
       * @return boolean
       */
      docketIsBeingPreviewed () {
        return this.previewingDocket.id === this.docket.id
      },

      /**
       * Inverse of above.
       *
       * @return boolean
       */
      docketIsNotBeingPreviewed () {
        return !this.docketIsBeingPreviewed
      },

      /**
       * Determine if not editing the docket.
       *
       * @return boolean
       */
      notEditingDocket () {
        return !this.editingDocket
      },

      /**
       * Determine if docket is pending.
       *
       * @return boolean
       */
      docketIsPending () {
        return this.docket.status === 'pending'
      },

      /**
       * Determine if docket is pending.
       *
       * @return boolean
       */
      docketIsResubmit () {
        return this.docket.status === 'resubmit'
      },

      /**
       * Decorated docket.
       *
       * @return object
       */
      decoratedDocket () {
        return new DocketDecorator(this.docket)
      },

      /**
       * URL to download the docket image.
       *
       * @return string
       */
      urlToDownloadDocket () {
        return url(`dockets/${this.docket.id}/download`)
      }
    },

    methods: {
      ...mapActions('weighbridge/docket', ['updateDocket', 'deleteDocket', 'approveDocket', 'rejectDocket']),
      ...mapMutations('weighbridge', [ADD_DOCKET, REMOVE_DOCKET, SET_WEIGHBRIDGE_ATTRIBUTE, SET_DOCKET_ATTRIBUTE]),
      ...mapMutations('weighbridge/docket', [SET_DOCKET]),
      ...mapMutations('load', [SET_LOAD_ATTRIBUTE, ADD_LOAD]),
      ...mapActions('load', ['getLoad', 'updateLoad']),

      updateLoadDeliveryTime () {
        if (this.load.delivery_time && this.load.delivery_date) {
          return Promise.resolve()
        }

        return this.updateLoad({
          id: this.load.id,
          payload: {
            delivery_date: moment().format('DD/MM/YYYY'), // 21/04/2019
            delivery_time: moment().format('hh:mm A') // 12:00 PM
          }
        }).then(({ data }) => {
          this[SET_LOAD_ATTRIBUTE]('delivery_date', data.delivery_date)
          this[SET_LOAD_ATTRIBUTE]('delivery_time', data.delivery_time)
          this[ADD_LOAD](this.load)
          this.$emit('update-delivery-time', {
            deliveryDate: data.delivery_date,
            deliveryTime: data.delivery_time
          })
        })
      },

      /**
       * Handle approving a docket.
       *
       * @return Promise
       */
      handleApproveDocket () {
        const notifyApproved = () => (notify.success('Docket approved.'))

        return this.approveDocket(this.docket.id)
          .then(this.updateLoadState)
          .then(notifyApproved)
      },

      /**
       * Handle rejecting a docket.
       *
       * @return Promise
       */
      handleRejectDocket () {
        const notifyRejected = () => (notify.success('Docket rejected.'))

        return alert.input('Enter a rejection reason', 'A rejection reason needs to be provided.').then(reason => {
          return this.rejectDocket({ id: this.docket.id, reason })
            .then(this.updateLoadState)
            .then(notifyRejected)
        })
      },

      /**
       * Handle previewing the docket.
       *
       * @return void
       */
      handlePreviewDocket () {
        this[SET_DOCKET](this.docket)
      },

      /**
       * Handle toggling the editing state of the docket.
       *
       * @return void
       */
      handleToggleEditDocket () {
        this.editingDocket = !this.editingDocket
      },

      /**
       * Handle saving the docket.
       *
       * @return Promise
       */
      handleSaveDocket () {
        let payload = {
          id: this.docket.id,
          docket: {
            number: this.docket.number,
            weight: this.docket.weight,
            file: this.docket.file || null
          }
        }

        return this.updateDocket(payload)
          .then(this.updateLoadState)
          .then(this.handleToggleEditDocket)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// LoadCheckModalItem.vue?309ee68a